import gql from 'graphql-tag'
import graphqlClient from '@shared/graphql/client'

export class SubprogramService {
  static async update(id, data, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_UPDATE(
          $id: String!
          $data: SubprogramInput!
          $manage: ManageInput!
        ) {
          subprogramUpdate(
            id: $id
            data: $data
            manage: $manage
          ) {
            id
          }
        }
      `,

      variables: {
        id,
        data,
        manage,
      },
    })

    return response.data.subprogramUpdate
  }

  static async changeStatus(id, status, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_CHANGE_STATUS(
          $id: String!
          $status: String!
          $manage: ManageInput!
        ) {
          subprogramChangeStatus(
            id: $id
            status: $status
            manage: $manage
          )
        }
      `,

      variables: {
        id,
        status,
        manage,
      },
    })

    return response.data.subprogramChangeStatus
  }

  static async destroyAll(ids, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_DESTROY(
          $ids: [String!]!
          $manage: ManageInput!
        ) {
          subprogramDestroy(ids: $ids, manage: $manage)
        }
      `,

      variables: {
        ids,
        manage,
      },
    })

    return response.data.subprogramDestroy
  }

  static async create(id, data, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_CREATE(
          $id: String!
          $data: SubprogramInput!
          $manage: ManageInput!
        ) {
          subprogramCreate(
            id: $id
            data: $data
            manage: $manage
          ) {
            id
          }
        }
      `,

      variables: {
        id,
        data,
        manage,
      },
    })

    return response.data.subprogramCreate
  }

  static async import(values, importHash, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_IMPORT(
          $data: SubprogramInput!
          $importHash: String!
          $manage: ManageInput!
        ) {
          subprogramImport(
            data: $data
            importHash: $importHash
            manage: $manage
          )
        }
      `,

      variables: {
        data: values,
        importHash,
        manage,
      },
    })

    return response.data.subprogramImport
  }

  static async find(id, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROGRAM_FIND(
          $id: String!
          $manage: ManageInput!
        ) {
          subprogramFind(id: $id, manage: $manage) {
            id
            name
            emails
            gender
            status
            identDocNeeded
            competitions
            regularSeasonConfigured
            playoffsStarted
            regularSeasonHasRounds
            endDate {
              year
              month
              day
            }
            startDate {
              year
              month
              day
            }
            category {
              id
              name
            }
            managers {
              id
              email
            }
            locations {
              id
              name
            }

            tryout {
              exist
              endDate {
                year
                month
                day
              }
              startDate {
                year
                month
                day
              }
            }
            payment {
              exist
              price
              defaultCurrency
            }
            additionalDoc {
              exist
              text
              requiredDocs {
                title
                file {
                  id
                  name
                  privateUrl
                  publicUrl
                  sizeInBytes
                }
              }
            }
            createdAt
            updatedAt
          }
        }
      `,

      variables: {
        id,
        manage,
      },
    })
    return response.data.subprogramFind
  }

  static async list(
    filter,
    orderBy,
    limit,
    offset,
    manage,
  ) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return { rows: [], count: 0 }

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROGRAM_LIST(
          $filter: SubprogramFilterInput
          $orderBy: SubprogramOrderByEnum
          $limit: Int
          $offset: Int
          $manage: ManageInput!
        ) {
          subprogramList(
            filter: $filter
            orderBy: $orderBy
            limit: $limit
            offset: $offset
            manage: $manage
          ) {
            count
            rows {
              id
              name
              emails
              gender
              status
              identDocNeeded
              competitions
              category {
                id
                name
              }
              managers {
                id
                email
              }
              locations {
                id
                name
              }
              tryout {
                exist
                endDate {
                  year
                }
                startDate {
                  year
                }
              }
              additionalDoc {
                exist
                text
              }
              regularSeasonRounds {
                id
              }
              updatedAt
              createdAt
            }
          }
        }
      `,

      variables: {
        filter,
        orderBy,
        limit,
        offset,
        manage,
      },
    })

    return response.data.subprogramList
  }

  static async listAutocomplete(query, limit, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROGRAM_AUTOCOMPLETE(
          $query: String
          $limit: Int
          $manage: ManageInput!
        ) {
          subprogramAutocomplete(
            query: $query
            limit: $limit
            manage: $manage
          ) {
            id
            label
          }
        }
      `,

      variables: {
        query,
        limit,
        manage,
      },
    })

    return response.data.subprogramAutocomplete
  }

  static async newId({ subpropertyId, programId }) {
    if (!subpropertyId && !programId) return

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROGRAM_NEW_ID(
          $subpropertyId: String!
          $programId: String!
        ) {
          subprogramNewId(
            subpropertyId: $subpropertyId
            programId: $programId
          )
        }
      `,
      variables: {
        subpropertyId,
        programId,
      },
    })
    return response.data.subprogramNewId
  }

  static async stripeData({ subpropertyId }) {
    if (!subpropertyId) return

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROPERTY_STRIPE_DATA(
          $subpropertyId: String!
        ) {
          stripeSubpropertyDataFind(
            subpropertyId: $subpropertyId
          ) {
            owqloFee {
              fixedAmount
              percentage
            }
            connectedAccount {
              address {
                country
              }
              defaultCurrency
            }
          }
        }
      `,

      variables: {
        subpropertyId,
      },
    })

    return response.data.stripeSubpropertyDataFind
  }

  static async startRegularSeason({ subpropertyId, programId, subprogramId }) {
    if (!subpropertyId || !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_START_REGULAR_SEASON(
          $subpropertyId: String!
          $programId: String!
          $subprogramId: String!
        ) {
          subprogramStartRegularSeason(
            subpropertyId: $subpropertyId
            programId: $programId
            subprogramId: $subprogramId
          ) 
        }
      `,

      variables: {
        subpropertyId,
        programId,
        subprogramId,
      },
    })

    return response.data.subprogramSstartRegularSeason
  }

  static async createRound(subprogramId, data, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId || !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_CREATE_ROUND(
          $subprogramId: String!
          $data: RegularSeasonRoundsInput!
          $manage: ManageInput!
        ) {
          subprogramCreateRound(
            subprogramId: $subprogramId
            data: $data
            manage: $manage
          ) {
            subprogramId
          }
        }
      `,

      variables: {
        subprogramId,
        data,
        manage,
      },
    })

    return response.data.subprogramCreateRound
  }

  static async findRound(id, subprogramId, manage) {
    const { subpropertyId, programId } = manage || {}
    if (!subpropertyId || !programId) return null

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROGRAM_FIND_ROUND(
          $id: String!
          $subprogramId: String!
          $manage: ManageInput!
        ) {
          subprogramFindRound(id: $id, subprogramId: $subprogramId, manage: $manage) {
            id
            startDate {
              year
              month
              day
            }
            endDate {
              year
              month
              day
            }
            hostingSchoolDivisionIndex
            gamesConfigurationString
          }
        }
      `,

      variables: {
        id,
        subprogramId,
        manage,
      },
    })
    return response.data.subprogramFindRound
  }

  static async listRound(subpropertyId, programId, subprogramId) {
    if (!subpropertyId) return []
    if (!programId) return []
    if (!subprogramId) return []

    const response = await graphqlClient.query({
      query: gql`
        query SUBPROGRAM_LIST_ROUND(
          $subpropertyId: String!,
          $programId: String!,
          $subprogramId: String!) 
        {
          subprogramListRound(
            subpropertyId: $subpropertyId,
            programId: $programId,
            subprogramId: $subprogramId)
          {
            id
            startDate {
              year
              month
              day
            }
            endDate {
              year
              month
              day
            }
            hostingSchoolDivisionIndex
            gamesConfigurationString
            order
          }
        }
      `,

      variables: {
        subpropertyId,
        programId,
        subprogramId,
      },
    })

    return response.data.subprogramListRound
  }

  static async updateRound(id, subprogramId, data, manage) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_UPDATE_ROUND(
          $id: String!
          $subprogramId: String!
          $data: RegularSeasonRoundsInput!
          $manage: ManageInput!
        ) {
          subprogramUpdateRound(
            id: $id
            subprogramId: $subprogramId
            data: $data
            manage: $manage
          ) {
            id
          }
        }
      `,

      variables: {
        id,
        subprogramId,
        data,
        manage,
      },
    })

    return response.data.subprogramUpdateRound
  }

  static async destroyRound(ids, subprogramId, manage) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_DESTROY_ROUND(
          $ids: [String!]!
          $subprogramId: String!
          $manage: ManageInput!
        ) {
          subprogramDestroyRound(ids: $ids, subprogramId: $subprogramId, manage: $manage)
        }
      `,

      variables: {
        ids,
        subprogramId,
        manage,
      },
    })

    return response.data.subprogramDestroyRound
  }

  static async startPlayoffs({ subpropertyId, programId, subprogramId }) {
    if (!subpropertyId || !programId) return

    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation SUBPROGRAM_START_PLAYOFFS_SEASON(
          $subpropertyId: String!
          $programId: String!
          $subprogramId: String!
        ) {
          subprogramStartPlayoffs(
            subpropertyId: $subpropertyId
            programId: $programId
            subprogramId: $subprogramId
          ) 
        }
      `,

      variables: {
        subpropertyId,
        programId,
        subprogramId,
      },
    })

    return response.data.subprogramStartPlayoffs // TODO
  }
}
